import dayjs from 'dayjs';
import lodash from 'lodash';
import _ from 'lodash';

const FORMAT_DATE_EN: string = 'YYYY-MM-DD';
const FORMAT_DATE_FR: string = 'DD/MM/YYYY';
const FORMAT_DATE_TIME_FR: string = 'DD/MM/YYYY hh:mm';

/**
 * Format a date with dayjs to specified format (default DD/MM/YYYY)
 *
 * @param {string | undefined} [date] The date to format
 * @param {string} [format=DD/MM/YYYY] The format to parse the date to
 * @returns {string} The formated date
 */
export function formatDate(date: string | undefined, format = FORMAT_DATE_FR): string {
	// If no date is provided, return empty string
	// else, format the date
	return lodash.isNil(date) ? '' : dayjs(date).format(format);
}

/**
 * Format a date with specific format with dayjs to specified format (default DD/MM/YYYY)
 *
 * @param {string | undefined} [date] The date to format
 * @param {string} [originFormat=DD/MM/YYYY hh:mm] The format to parse the date to
 * @param {string} [format=DD/MM/YYYY] The format to parse the date to
 * @returns {string} The formated date
 */
export function formatDateWithOrigin(
	date: string | undefined,
	originFormat = FORMAT_DATE_TIME_FR,
	format = FORMAT_DATE_FR): string {
	// If no date is provided, return empty string
	// else, format the date
	return lodash.isNil(date) ? '' : dayjs(date, originFormat).format(format);
}

/**
 * Format a date with dayjs to specified format (default DD/MM/YYYY)
 *
 * @param {number | undefined} [date] The date to format
 * @param {string} [format=DD/MM/YYYY] The format to parse the date to
 * @returns {string} The formated date
 */
export function formatDateTimestamp(date: number | undefined, format = FORMAT_DATE_FR): string {
	// If no date is provided, return empty string
	// else, format the date
	return lodash.isNil(date) ? '' : dayjs(date).format(format);
}

/**
 * Format a date with time to DD/MM/YYYY HH:mm format
 * @param {string} [date] The date to format
 * @returns {string} The formatted date
 */
export function formatDateTime(date: string | undefined): string {
	return formatDate(date, FORMAT_DATE_TIME_FR);
}

/**
 * Format a date with time to DD/MM/YYYY HH:mm format
 * @param {number} [date] The date to format
 * @returns {string} The formatted date
 */
export function formatDateTimeTimestamp(date: number | undefined): string {
	return formatDateTimestamp(date, FORMAT_DATE_TIME_FR);
}

/**
 * Format a date with dayjs to specified format (default DD/MM/YYYY)
 *
 * @param {dayjs.Dayjs} [date] The date to format
 * @param {string} [format=DD/MM/YYYY] The format to parse the date to
 * @returns {string} The formated date
 */
export function formatDayjs(date: dayjs.Dayjs | undefined, format = FORMAT_DATE_FR): string {
	// If no date is provided, return empty string
	// else, format the date
	return date === undefined ? '' : date.format(format);
}

/**
 * Format a date with dayjs to specified format (default DD/MM/YYYY)
 *
 * @param {dayjs.Dayjs} [date] The date to format
 * @param {string} [format=DD/MM/YYYY] The format to parse the date to
 * @returns {string} The formated date
 */
export function formatDateStandard(date: dayjs.Dayjs | undefined, format = FORMAT_DATE_FR): string {
	// If no date is provided, return empty string
	// else, format the date
	return date === undefined ? '' : date.format(format);
}

/**
 * Format a date with dayjs to specified format (default HH:mm)
 *
 * @param {dayjs.Dayjs} [date] The date to format
 * @param {string} [format=HH:mm] The format to parse the date to
 * @returns {string} The formated date
 */
export function formatDateSchedule(date: dayjs.Dayjs | undefined, format = 'HH:mm'): string {
	// If no date is provided, return empty string
	// else, format the date
	return date === undefined ? '' : date.format(format);
}

/**
 * Format a date with dayjs to specified format (default YYYY-MM-DD)
 *
 * @param {dayjs.Dayjs} [date] The date to format
 * @param {string} [format=YYYY-MM-DD] The format to parse the date to
 * @returns {string} The formated date
 */
export function formatDateISO(date: dayjs.Dayjs | undefined, format = FORMAT_DATE_EN): string {
	// If no date is provided, return empty string
	// else, format the date
	return date === undefined ? '' : date.format(format);
}

/**
 * Format a date with dayjs to specified format (default YYYY-MM-DD)
 *
 * @param {string} [date] The date to format
 * @param {string} [format=YYYY-MM-DD] The format to parse the date to
 * @returns {dayjs.Dayjs} The formated date
 */
export function formatISOStringToDate(date: string | undefined, format = FORMAT_DATE_EN): dayjs.Dayjs {
	return dayjs(date, format);
}

/**
 * Format a date into ISO dateTime format
 * @param {string} [date] The date to format
 * @param {string} [originFormat=DD/MM/YYYY hh:mm] The format to parse the date to
 * @returns {string} The formated date
 */
export function formatDateWithOriginToIso(date: string | undefined, originFormat = FORMAT_DATE_TIME_FR): string {
	return formatDateWithOrigin(date, originFormat, 'YYYY-MM-DDTHH:mm:ss.SSS');
}

/**
 * Format a date with dayjs to specified format (default DD/MM/YYYY)
 *
 * @param {string} [date] The date to format
 * @param {string} [format=DD/MM/YYYY] The format to parse the date to
 * @returns {dayjs.Dayjs} The formated date
 */
export function formatStandardStringToDate(date: string | undefined, format = FORMAT_DATE_FR): dayjs.Dayjs {
	return dayjs(date, format);
}

/**
 * Format a String date from a format to another
 * Default DD/MM/YYYY => YYYY-MM-DD
 *
 * @param {string} [date] The date to format
 * @param {string} [fromFormat=YYYY-MM-DD] The current format of the date
 * @param {string} [toFormat=YYYY-MM-DD] The new format of the date
 * @returns {string} The formatted date
 */
export function changeFormatDateString(
	date: string | undefined, fromFormat = FORMAT_DATE_FR, toFormat = FORMAT_DATE_EN): string {
	return dayjs(date, fromFormat).format(toFormat);
}

/**
 * Format a date with time to HH:mm format
 * @param {string} [date] The date to format
 * @returns {string} The formatted hours
 */
export function formatHours(date: string | undefined): string {
	return formatDate(date, 'HH:mm');
}

/**
 * Format a date with time to HH:mm format
 * @param {string} [date] The date to format
 * @param {string} [originFormat=DD/MM/YYYY hh:mm] The format to parse the date to
 * @returns {string} The formatted hours
 */
export function formatHoursWithOrigin(date: string | undefined, originFormat = FORMAT_DATE_TIME_FR): string {
	return formatDateWithOrigin(date, originFormat, 'HH:mm');
}

/**
 * Formate une date au format YYYYMMDD dont le mois est supérieur à 12
 * @param date date lunaire au format DD/MM/YYYY
 */
export function formatDateLunaire(date: string): string {
	return _.replace(date, /(\d{4})(\d{2})(\d{2})/, '$3/$2/$1');
}
